'use client';

import { c as _c } from "react/compiler-runtime";
import { type Editor } from '@tiptap/react';
import { Bold, Code, Heading, Italic, Link2, List, ListOrdered, Quote, Strikethrough } from 'lucide-react';
import { useRef } from 'react';
import { TOOLBAR_ICON, toolbarButton } from './toolbarButton';
type Tool = {
  title: string;
  Icon: typeof Bold;
  run: (editor: Editor) => void;
  isActive: (editor: Editor) => boolean;
  /** Shown in the tooltip; TipTap binds the key itself. */
  shortcut?: string;
};
type Divider = {
  divider: true;
};
const isTool = (entry: Tool | Divider): entry is Tool => !('divider' in entry);

// In Slack's order. `isActive` lights the button when the caret is already
// inside that mark, which is what makes a toggle feel like a toggle.
const TOOLS: (Tool | Divider)[] = [{
  title: 'Bold',
  Icon: Bold,
  shortcut: '⌘B',
  run: e => e.chain().focus().toggleBold().run(),
  isActive: e => e.isActive('bold')
}, {
  title: 'Italic',
  Icon: Italic,
  shortcut: '⌘I',
  run: e => e.chain().focus().toggleItalic().run(),
  isActive: e => e.isActive('italic')
}, {
  title: 'Strikethrough',
  Icon: Strikethrough,
  shortcut: '⇧⌘X',
  run: e => e.chain().focus().toggleStrike().run(),
  isActive: e => e.isActive('strike')
}, {
  title: 'Code',
  Icon: Code,
  shortcut: '⌘E',
  run: e => e.chain().focus().toggleCode().run(),
  isActive: e => e.isActive('code')
}, {
  divider: true
}, {
  title: 'Link',
  Icon: Link2,
  shortcut: '⌘K',
  run: e => {
    if (e.isActive('link')) {
      e.chain().focus().unsetLink().run();
      return;
    }
    const href = window.prompt('Link URL', 'https://');
    if (href) e.chain().focus().setLink({
      href
    }).run();
  },
  isActive: e => e.isActive('link')
}, {
  title: 'Heading',
  Icon: Heading,
  run: e => e.chain().focus().toggleHeading({
    level: 2
  }).run(),
  isActive: e => e.isActive('heading')
}, {
  title: 'Quote',
  Icon: Quote,
  run: e => e.chain().focus().toggleBlockquote().run(),
  isActive: e => e.isActive('blockquote')
}, {
  divider: true
}, {
  title: 'Bulleted list',
  Icon: List,
  shortcut: '⇧⌘8',
  run: e => e.chain().focus().toggleBulletList().run(),
  isActive: e => e.isActive('bulletList')
}, {
  title: 'Numbered list',
  Icon: ListOrdered,
  shortcut: '⇧⌘7',
  run: e => e.chain().focus().toggleOrderedList().run(),
  isActive: e => e.isActive('orderedList')
}];

/**
 * The formatting buttons for a `PostInput`'s editor, rendered by the surface
 * that owns the toolbar row so there is never a second row of icons.
 *
 * One roving-tabindex toolbar: nine separate tab stops made Tab out of the
 * editor feel like it was walking the buttons one at a time.
 */
const FormatTools = t0 => {
  const $ = _c(4);
  const {
    editor
  } = t0;
  const barRef = useRef(null);
  if (!editor) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = TOOLS.filter(isTool);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const buttons = t1;
  const moveFocus = (from, delta) => {
    const next = (from + delta + buttons.length) % buttons.length;
    barRef.current?.querySelectorAll("button[data-format-tool]")[next]?.focus();
  };
  const ringIndex = _temp;
  const t2 = "toolbar";
  const t3 = "Formatting";
  const t4 = "flex flex-wrap items-center gap-0.5";
  const t5 = TOOLS.map((entry, key) => {
    if (!isTool(entry)) {
      return <span key={`divider-${key}`} className="mx-0.5 h-5 w-px shrink-0 bg-ds-border-standard" />;
    }
    const at_0 = ringIndex(key);
    const active = entry.isActive(editor);
    return <button key={entry.title} type="button" data-format-tool={true} tabIndex={at_0 === 0 ? 0 : -1} title={entry.shortcut ? `${entry.title} (${entry.shortcut})` : entry.title} aria-label={entry.title} aria-pressed={active} onKeyDown={e => {
      if (e.key === "ArrowRight") {
        moveFocus(at_0, 1);
      } else {
        if (e.key === "ArrowLeft") {
          moveFocus(at_0, -1);
        } else {
          return;
        }
      }
      e.preventDefault();
    }} onMouseDown={_temp2} onClick={() => entry.run(editor)} className={toolbarButton(active)}><entry.Icon size={TOOLBAR_ICON} /></button>;
  });
  let t6;
  if ($[1] !== barRef || $[2] !== t5) {
    t6 = <div ref={barRef} role={t2} aria-label={t3} className={t4}>{t5}</div>;
    $[1] = barRef;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  return t6;
};
export default FormatTools;
function _temp(at) {
  return TOOLS.slice(0, at).filter(isTool).length;
}
function _temp2(e_0) {
  return e_0.preventDefault();
}